<template>
  <div>
    <!---------------------- MISC ----------------------->
    <div class="mb-5"
      v-if="selectedTab.type == ItemCategoryTypes.MISC || selectedViewType != viewTypes.tabbedView">
      <quote-builder-misc-headings-component :name="miscCategoryObject?.displayName || 'Miscellaneous'"
        :count="miscLength"
        :total-amount="MiscTotal"
        :is-full-width="isFullWidth" />
      <div class="rows">
        <draggable @change="itemMoved($event, ItemCategoryTypes.MISC)"
          v-model="innerValue.miscs"
          @over.prevent
          @enter.prevent
          v-bind="{'disabled': !isRowDraggable, chosen: 'chosen', ghostClass: 'draggable-ghost', dragClass: 'draggable-drag'}"
          :clone="$event => cloneHandler($event, ItemCategoryTypes.MISC)"
          :group="{ name: ItemCategoryTypes.MISC, pull: 'clone', put: true }"
          handle=".item-drag-handle">
          <quote-builder-misc-item v-for="item in miscItems"
            :key="item.id"
            :inner-value="innerValue"
            :is-full-width="isFullWidth"
            :items-assessments="itemsAssessments"
            :item="item"
            :edit-item-id="editItemObj.id"
            :vehicle="vehicle"
            :is-editable="!readOnlyComputed"
            @deleteItem="item => deleteItem(item)"
            @focusNextRow="$emit('focusNextRow', item)"
            @handlePresentAs="itemId => $emit('handlePresentAs', itemId)"
            @itemRowClicked="(value, event, close) => $emit('handleItemRowClick',value, event, close)" />
        </draggable>
      </div>
      <div>
        <button class="button is-outlined is-primary"
          @click="addBlankItem()"
          :disabled="readOnlyComputed">
          + Add Item
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import QuoteBuilderMiscHeadingsComponent from './QuoteBuilderMiscHeadingsComponent.vue'
import QuoteBuilderMiscItem from './QuoteBuilderMiscItem.vue'
import draggable from 'vuedraggable'
import { ItemCategoryTypes, EventHubTypes } from '@/enums'
import cloneDeep from 'lodash.clonedeep'
import { QuoteItemModel } from '@/classes/viewmodels'
import { QuoteTotalsMethodMixin, QuoteAssessmentMixin, QuoteItemValidationMixin, QuoteItemAutoSaveDeleteMixin } from '../../mixins'

export default {
  name: 'QuoteBuilderMiscComponent',
  components: {
    QuoteBuilderMiscHeadingsComponent,
    QuoteBuilderMiscItem,
    draggable
  },
  mixins: [QuoteTotalsMethodMixin, QuoteAssessmentMixin, QuoteItemValidationMixin, QuoteItemAutoSaveDeleteMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    isFullWidth: {
      type: Boolean,
      required: true
    },
    editItemObj: {
      type: Object
    },
    selectedTab: {
      type: Object
    },
    selectedViewType: {
      type: String
    },
    isAudanet: {
      type: Boolean,
      default: false
    },
    viewTypes: {
      type: Object
    },
    vehicle: {
      type: Object
    },
    nextLineNumber: {
      type: Number,
      default: 0
    },
    isEditable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      innerValue: null,
      isSyncingParentUpdate: false,
      lastEmittedMiscsSignature: null
    }
  },
  computed: {
    ItemCategoryTypes() {
      return ItemCategoryTypes
    },
    miscItems() {
      return this.innerValue.miscs
    },
    miscLength() {
      return this.innerValue.miscs.filter((i) => !(i.deleted || i.isDeleted)).length
    },
    MiscTotal() {
      return this.calculateItemCategoryTotal(this.innerValue.quotingMethod, this.innerValue.miscs, ItemCategoryTypes.MISC)
    },
    isRowDraggable() {
      return !this.readOnlyComputed
    },
    itemsAssessments() {
      return this.getItemsAssessments(this.innerValue.miscs, this.innerValue.quoteAssessments)
    },
    miscCategoryObject() {
      return this.$company.itemCategories.find((i) => i.itemCategoryType === ItemCategoryTypes.MISC)
    },
    readOnlyComputed() {
      return this.innerValue.readOnly || !this.isEditable
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) return
        const currentSignature = this.computeMiscsSignature(newVal.miscs)
        if (this.isSyncingParentUpdate && currentSignature === this.lastEmittedMiscsSignature) {
          this.isSyncingParentUpdate = false
          return
        }
        // Fast-path: skip deepDiff(entire_entity) when only sibling-derived totals changed
        const innerMiscsSignature = this.computeMiscsSignature(this.innerValue?.miscs)
        if (currentSignature === innerMiscsSignature &&
            this.innerValue?.quotingMethod === newVal.quotingMethod &&
            this.innerValue?.readOnly === newVal.readOnly) {
          return
        }
        this.innerValue = cloneDeep(newVal)
      },
      deep: true
    },
    innerValue: {
      handler(newVal) {
        if (!newVal || this.isSyncingParentUpdate) return
        const nextSignature = this.computeMiscsSignature(newVal.miscs)
        if (nextSignature === this.lastEmittedMiscsSignature) return
        this.lastEmittedMiscsSignature = nextSignature
        this.isSyncingParentUpdate = true
        // Merge with latest parent value to avoid emitting stale sibling arrays (Parts, Misc, etc.) when only labours changed.
        const toEmit = { ...this.value, miscs: cloneDeep(newVal.miscs) }
        this.$emit('handleUpdate', toEmit)
      },
      deep: true
    }
  },
  created() {
    this.innerValue = cloneDeep(this.value)
    this.lastEmittedMiscsSignature = this.computeMiscsSignature(this.value?.miscs)
    // Value is committed to vm.entity after the field's model-update delay.
    // The item watcher in QuoteView triggers autosave once the value propagates.
    this.$watch('innerValue', function () {
    }, { deep: true })
  },
  mounted() {
    this.$v.entity.miscs.$touch()
    // event hub to update the miscs
    this.$eventHub.$on('updateMiscs', this.updateMiscs)
    this.$eventHub.$on('addBlankItemMisc', this.addBlankItem)
    this.$eventHub.$on('deleteMisc', this.deleteItem)
  },
  beforeDestroy() {
    this.$eventHub.$off('updateMiscs', this.updateMiscs)
    this.$eventHub.$off('addBlankItemMisc', this.addBlankItem)
    this.$eventHub.$off('deleteMisc', this.deleteItem)
  },
  methods: {
    computeMiscsSignature(items = []) {
      try {
        return JSON.stringify(items)
      } catch (e) {
        return null
      }
    },
    updateMiscs() {
      const newItems = this.innerValue.miscs.filter((i) => i.isNew)

      this.innerValue = cloneDeep(this.value)

      // if there are new items, add them to the innerValue
      if (newItems.length) {
        newItems.forEach((item) => {
          // check if the item already exists in the innerValue
          const existingItem = this.innerValue.miscs.find((i) => i.itemDesc === item.itemDesc && i.itemNo === item.itemNo)
          if (!existingItem && item.quoteItemId == this.editItemObj.id) {
            // if it doesn't exist, add it
            this.innerValue.miscs.push(item)
          }
        })
      }

      // compare two objects to see if they are the same, sometimes the parent
      // value might be updated by innervalue and we don't want to update the inner value again
      // const diff = deepDiff(this.innerValue, this.value)
      // if (diff) {
      //   // check if changes in parent/child components
      //   const hasMiscsDiff = diff.some((d) => d.path && d.path[0] === 'miscs')

      //   const newInnerValue = cloneDeep(this.value)
      //   // if parent/child miscs arrays are different, merge them
      //   if (hasMiscsDiff) {
      //     // items that exist in child but not in parent (could be added when tabbing, enter or arrow down)
      //     const newMiscsItems = this.innerValue.miscs.filter((i) => i.isNew)
      //     // if these items don't exist in the new value, add them
      //     newMiscsItems.forEach((item) => {
      //       if (!newInnerValue.miscs.find((i) => i.itemDesc === item.itemDesc && i.itemNo === item.itemNo)) {
      //         newInnerValue.miscs.push(item)
      //       }
      //     })
      //   }
      //   this.innerValue = newInnerValue
      // }
    },
    cloneHandler(item, type) {
      // change the quoteItemId to a new guid so we can find it later and change to correct type on drop
      // TODO: looks like this doesn't work, leave it as is
      // item.quoteItemId = Guid.newGuid()
      return item
    },
    itemMoved(event, type) {
      // if an item was dragged from one list to another, i.e from RR to RWA, Parts to RR etc.
      if (event?.added) {
        const item = event.added.element
        this.handleItemAdded(item, type, event.added.newIndex)
      }

      let sortNo = this.innerValue.miscs.length ? Math.min(...this.innerValue.miscs.map((i) => i.sortNo || 1)) : 1
      this.innerValue.miscs.forEach((item) => {
        item.sortNo = sortNo
        sortNo++
      })
      this.$eventHub.$emit(EventHubTypes.AutoSaveQuoteItems)
      // Emit directly — don't rely on the deep watcher to detect in-place sortNo mutations
      const nextSignature = this.computeMiscsSignature(this.innerValue.miscs)
      this.lastEmittedMiscsSignature = nextSignature
      this.isSyncingParentUpdate = true
      this.$emit('handleUpdate', { ...this.value, miscs: cloneDeep(this.innerValue.miscs) })
      this.$nextTick(() => { this.isSyncingParentUpdate = false })
    },

    handleItemAdded(item, type, index) {
      let newItem = new QuoteItemModel(this.innerValue.quoteId, '', item.itemDesc, ItemCategoryTypes.MISC)
      newItem.lineNumber = !this.isAudanet ? this.nextLineNumber : 0
      newItem.itemNo = this.$filters.pad(newItem.lineNumber, 4)
      newItem.value = 0
      newItem.side = ''
      newItem.sortNo = this.innerValue.miscs.length ? Math.max(...this.innerValue.miscs.map((i) => i.sortNo)) + 1 : 1
      this.innerValue.lines = this.nextLineNumber + 1
      // replace the item with the new item
      this.innerValue.miscs.splice(index, 1, newItem)

      this.$emit('onItemCopied', newItem)
    },

    addBlankItem() {
      let newItem = new QuoteItemModel(this.innerValue.quoteId, '', '', ItemCategoryTypes.MISC)
      newItem.lineNumber = !this.isAudanet ? this.nextLineNumber : 0
      newItem.itemNo = this.$filters.pad(newItem.lineNumber, 4)
      newItem.value = 0
      newItem.side = ''
      newItem.sortNo = this.innerValue.miscs.length ? Math.max(...this.innerValue.miscs.map((i) => i.sortNo)) + 1 : 1
      this.innerValue.miscs.splice(this.innerValue.miscs.length, 1, newItem)
      this.innerValue.lines = this.nextLineNumber + 1
      this.$notification.success('', 'Item added')

      // this.$toast.open({
      //   message: 'Item added',
      //   type: 'is-success',
      //   position: 'is-bottom',
      //   queue: false
      // })

      this.$nextTick(() => {
        this.$emit('newItemAdded', newItem.quoteItemId)
      })
    },
    deleteItem(item) {
      if (item.isNew) {
        const itemIndex = this.innerValue.miscs
          .map(function (obj) {
            return obj.quoteItemId
          })
          .indexOf(item.quoteItemId)
        if (itemIndex >= 0) {
          this.innerValue.miscs.splice(itemIndex, 1)
          // this.items.splice(index, 1)
        }
      } else {
        this.autoSaveDeleteExistingItem(item)
      }

      // Sync the deletion into the Add-Item panel so it isn't re-added.
      this.$eventHub.$emit(EventHubTypes.QuoteItemDeletedSync, item)

      // Propagate the deletion to the parent directly (mirrors itemMoved) rather than
      // relying on the innerValue watcher, which drops emits while isSyncingParentUpdate
      // is true — otherwise a later value echo resets innerValue and resurrects the item.
      this.lastEmittedMiscsSignature = this.computeMiscsSignature(this.innerValue.miscs)
      this.isSyncingParentUpdate = true
      this.$emit('handleUpdate', { ...this.value, miscs: cloneDeep(this.innerValue.miscs) })
      this.$nextTick(() => { this.isSyncingParentUpdate = false })

      this.$emit('onItemDeleted', item.lineNumber)

      // this.updateTotal()
      // this.$toast.open({
      //   message: `Item <span class="is-italic">${item.itemDesc}</span> deleted`,
      //   type: 'is-danger',
      //   position: 'is-top',
      //   actionText: 'Undo',
      //   onAction: () => {
      //     if (item.isNew) {
      //       this.innerValue.miscs.splice(this.innerValue.miscs.length, 1, item)
      //       // this.items.splice(this.items.length, 1, item)
      //     } else {
      //       item.deleted = false
      //       item.isDeleted = false
      //       item.itemStatus = originalItemStatus
      //     }
      //     // this.updateTotal()
      //   }
      // })
    }
  }
}
</script>

<style scoped>
/* Your component's styles go here */
</style>